import React from 'react';
import PropTypes, {object} from 'prop-types';
import {injectIntl} from 'react-intl';

import Spinner from '@stubhub/react-spinner';

import Layer from './classes/layer';
import Map from './classes/map';
import MAP_TYPE from './enums/mapType';
import {
  layerExists,
  getUniqueSeatIdsFromListing,
  getListingIdFromListing,
  seatProps,
  getListingFromSectionRowSeatIds,
  getListingsFromSectionRowIds,
  splitVectorMinimumQuantity,
  venueConfigId,
  getRowFeatureByUniqueRowId,
  getSeatFeatureByUniqueSeatId,
  getsCoordsOfAGroupOfSeats,
  getCenterOfListing,
  tapArea,
  urlWithMode,
  addMapSource,
  getAvailableSectionsForZones,
} from './helper-functions/mapbox_helpers';
import BackgroundLayers from './layers/layer_background';
import LabelLayers from './layers/layer_labels';
import RowLayers, {rowsFillColor} from './layers/layer_rows';
import SeatLayers, {seatsCircleColor} from './layers/layer_seats';
import SectionLayers, {sectionsStylesGenerator} from './layers/layer_sections';
import {
  TYPE_SECTION,
  TYPE_ROW,
  TYPE_SEAT,
  COLOR_UNAVAILABLE_SECTION,
  COLOR_NONGA_SECTION,
  LAYER_SECTIONS_ID,
  LAYER_ROWS_ID,
  LAYER_SECTIONS_OUTLINE_ID,
  LAYER_SEATS_HIGHLIGHTED_ID,
  LAYER_SEATS_ID,
  LAYER_SECTIONS_HIGHLIGHTED,
  LAYER_SECTIONS_GA,
  DEFAULT_POPUP_WIDTH,
  DEFAULT_ANCHOR_POSITION,
  CLICK_TOOLTIP_CLASSNAME,
  ROW_TOOLTIP_CLASSNAME,
  LAYER_SECTIONS_HOVERED,
} from './mapbox-constants/mapbox_constants';

import '../styles/seatMapMapbox.scss';
import 'mapbox-gl/dist/mapbox-gl.css';

class MapboxVoidSSR {}
const mapboxgl = __CLIENT__ ? require('mapbox-gl') : {Popup: MapboxVoidSSR};

const MAPBOX_DOM_CONTAINER_ID = 'SeatMapMapbox';
const MAPBOX_DOM_CONTAINER_ID_BYOVIEW = 'SeatMapMapboxBYO';

class SeatMapMapboxView extends React.Component {
  constructor(props) {
    super(props);
    const {mapConfigData, generatePopup} = this.props;
    const offset = mapConfigData.OFFSET_TOOTIP;

    this.tapPaddingWidth = mapConfigData.CLICK_PADDING_WIDTH;
    this.tapPaddingHeight = mapConfigData.CLICK_PADDING_HEIGHT;
    this.clickTooltip = generatePopup(
      false,
      false,
      offset,
      DEFAULT_POPUP_WIDTH,
      DEFAULT_ANCHOR_POSITION,
      CLICK_TOOLTIP_CLASSNAME
    );
    this.rowTooltip = generatePopup(
      false,
      false,
      offset,
      DEFAULT_POPUP_WIDTH,
      DEFAULT_ANCHOR_POSITION,
      ROW_TOOLTIP_CLASSNAME
    );

    this.needsUpdateRender = false;

    this.state = {
      /* eslint-disable react/no-unused-state */
      sectionClicked: true,
      seatClickedFromMap: false,
      GASectionIdClickedFromList: null,
      rowClickedFromList: null,
      usidsOfListingSelected: null,
      /* eslint-enable react/no-unused-state */
    };
  }

  static contextTypes = {
    globalRegistry: PropTypes.object,
    router: PropTypes.object,
  };

  initMap = () => {
    const {
      type,
      device,
      mapConfigData,
      listingInfo,
      setLoadedState,
      setRowCenteredOnMapState,
      handleSectionMouseMove,
      handleSectionMouseLeave,
      hasReset,
    } = this.props;

    const layerId = type === TYPE_ROW ? LAYER_ROWS_ID : LAYER_SEATS_HIGHLIGHTED_ID;
    this.featureRecorder = type === TYPE_ROW ? this.recordRowFeatures : this.recordSeatCoords;
    if (!(type === TYPE_SEAT || type === TYPE_ROW)) {
      this.mapInstance.setMaxZoom(mapConfigData.ZOOM_MAX_ZOOMLEVEL_SECTION);
    }

    window.addEventListener('resize', this.actions.flyToHome);

    this.mapInstance.once('load', () => {
      this.mapInstance.getCanvas().style.cursor = listingInfo ? 'default' : 'move';

      setLoadedState(true);
      this.mapInstance.dragRotate.disable();

      if (listingInfo) {
        this.mapInstance.scrollZoom.disable();
        this.mapInstance.boxZoom.disable();
        this.mapInstance.dragPan.disable();
        this.mapInstance.doubleClickZoom.disable();
        this.mapInstance.touchZoomRotate.disable();
      }

      this.mapInstance.once('idle', this.initMapSetup);
      if (device === 'desktop' && !listingInfo) {
        this.mapInstance.addControl(new mapboxgl.NavigationControl({showCompass: false}), 'bottom-right');
        if (type === TYPE_SECTION) {
          this.mapInstance.on('mousemove', LAYER_SECTIONS_HIGHLIGHTED, handleSectionMouseMove);
          this.mapInstance.on('mouseleave', LAYER_SECTIONS_HIGHLIGHTED, handleSectionMouseLeave);
        } else if (type === TYPE_ROW) {
          this.mapInstance.on('mouseenter', LAYER_ROWS_ID, this.handleRowOnHover);
          this.mapInstance.on('mouseleave', LAYER_ROWS_ID, this.handleRowOffHover);
        }
      }

      this.mapInstance.on('zoomend', () => {
        if (this.mapInstance.getZoom() < mapConfigData.ZOOM_TOOLTIP_BREAKPOINT && this.clickTooltip.isOpen()) {
          this.clickTooltip.remove();
        }
      });

      this.mapInstance.on('movestart', () => {
        setRowCenteredOnMapState(false);
      });

      this.mapInstance.on('moveend', () => {
        const sections = this.renderedSections;
        if (this.mapInstance.getZoom() > mapConfigData.ZOOM_MAX_FITBOUNDS_SEATS) {
          if (layerExists(this.mapInstance, layerId)) {
            const queriedFeatures = this.mapInstance.queryRenderedFeatures({layers: [layerId]});
            this.featureRecorder(queriedFeatures);
            this.updateSeatInventory(this.props, sections);
          }
        }
        this.updateRender();

        this.mapInstance.once('idle', () => {
          if (this.shouldResetButtonAdd() && !listingInfo) {
            this.addResetControl();
          } else if (hasReset) {
            this.removeResetControl();
          }
        });
      });
    });
  };

  initMapSetup = () => {
    const {device, listingId, fetchSelectedListing, eventId, getQuery, shstore} = this.props;
    if (device === 'desktop') {
      this.actions.flyToHome();
      if (listingId) {
        fetchSelectedListing(eventId, {...getQuery(this.props), ...{listingId, shstore}});
      }
    } else if (device === 'phone') {
      this.actions.mobileFlyToHome();
    }
    // Though this var is not used, the set method has some side effects
    // eslint-disable-next-line react/no-unused-class-component-methods
    this.sectionCoords = this.actions.queryRenderedFeatures({layers: [LAYER_SECTIONS_HIGHLIGHTED]});
  };

  sectionFeaturesCache = {};
  GASections = {};
  // eslint-disable-next-line react/no-unused-class-component-methods
  set sectionCoords(features) {
    const {availableSectionsLookUp} = this.props;
    for (let i = 0; i < features.length; i++) {
      const {sectionId, ga} = features[i].properties;
      this.sectionFeaturesCache[sectionId] = features[i];
      if (ga === true && availableSectionsLookUp[sectionId]) {
        this.GASections[sectionId] = true;
      }
    }
  }

  listingsCenterCoordCache = {};
  NumbersOfListingsVisibleSeatsCache = {};
  seatCoordCache = {};
  recordListingsCenter = (mapSectionRowSeatIdsToListingId) => {
    if (mapSectionRowSeatIdsToListingId) {
      const listingIds = Object.keys(mapSectionRowSeatIdsToListingId);
      if (listingIds.length > 0) {
        for (let i = 0; i < listingIds.length; i++) {
          if (mapSectionRowSeatIdsToListingId[listingIds[i]]) {
            const uniqueSeatIdsArray = Object.keys(mapSectionRowSeatIdsToListingId[listingIds[i]]);
            const listingVisibleCoords = getsCoordsOfAGroupOfSeats(this.seatCoordCache, uniqueSeatIdsArray);
            const center = getCenterOfListing(this.seatCoordCache, uniqueSeatIdsArray);
            let isListingVisibleCoordsUpdated = false;
            if (
              !this.NumbersOfListingsVisibleSeatsCache[listingIds[i]] ||
              listingVisibleCoords.length > this.NumbersOfListingsVisibleSeatsCache[listingIds[i]]
            ) {
              this.NumbersOfListingsVisibleSeatsCache[listingIds[i]] = listingVisibleCoords.length;
              isListingVisibleCoordsUpdated = true;
            }
            if (center && isListingVisibleCoordsUpdated) {
              this.listingsCenterCoordCache[listingIds[i]] = center;
            }
          }
        }
      }
    }
  };

  recordSeatCoords = (features) => {
    for (let i = 0; i < features.length; i++) {
      const {usid} = features[i].properties;
      const {coordinates} = features[i].geometry;
      if (!this.seatCoordCache[usid]) {
        this.seatCoordCache[usid] = coordinates;
      }
    }
  };

  rowFeaturesCache = {};
  recordRowFeatures = (features) => {
    for (let i = 0; i < features.length; i++) {
      const {urid} = features[i].properties;
      if (!this.rowFeaturesCache[urid]) {
        this.rowFeaturesCache[urid] = features[i];
      }
    }
  };

  availableSections = () => {
    const {availableSections, listingInfo} = this.props;
    const sectionsIfListingInfo =
      listingInfo &&
      (listingInfo.sectionId ? [listingInfo.sectionId] : this.getSectionIdsFromZones(listingInfo.zoneId));

    return listingInfo ? sectionsIfListingInfo : availableSections;
  };

  selectedGASectionId = () => {
    const {sectionIdList, GASectionIdClickedFromList} = this.props;
    if (GASectionIdClickedFromList) {
      return GASectionIdClickedFromList;
    } else if (this.GASections[sectionIdList]) {
      return sectionIdList;
    }

    return null;
  };

  selectedSectionIdList = () => {
    const {sectionIdList, GASectionIdClickedFromList} = this.props;
    if (GASectionIdClickedFromList) {
      return GASectionIdClickedFromList;
    }
    if (!Array.isArray(sectionIdList)) {
      return [sectionIdList];
    }

    return sectionIdList;
  };

  hoveredSectionIdList = () => {
    const {device, hoveredListingSid, listingSelected, listingInfo, hoveredSections, hoveredListingZid} = this.props;
    const listingSelectedSection = listingSelected ? listingSelected.sectionId : null;
    const listingSelectedZone = listingSelected ? listingSelected.zoneId : null;
    const listingInfoSection = listingInfo ? listingInfo.venueConfigSectionId : null;
    const mergedSections = {};
    hoveredSections.forEach((sectionId) => {
      mergedSections[sectionId.toString()] = '';
    });
    if (!listingInfoSection && device === 'desktop') {
      if (listingSelectedSection) {
        mergedSections[listingSelectedSection.toString()] = '';
      }
      if (!listingSelectedSection && listingSelectedZone && !listingInfo) {
        const sectionIds = this.getSectionIdsFromZones([listingSelectedZone]);
        sectionIds.forEach((sectionId) => {
          mergedSections[sectionId.toString()] = '';
        });
      }
      if (hoveredListingSid) {
        mergedSections[hoveredListingSid.toString()] = '';
      }
      if (hoveredListingZid && !hoveredListingSid) {
        const sectionIds = this.getSectionIdsFromZones([hoveredListingZid]);
        sectionIds.forEach((sectionId) => {
          mergedSections[sectionId.toString()] = '';
        });
      }
    }

    return Object.keys(mergedSections);
  };

  selectedRow = () => {
    const {sectionIdList, listingRows, rowClickedFromList} = this.props;
    if (rowClickedFromList) {
      return rowClickedFromList;
    } else if (sectionIdList && listingRows) {
      return `${sectionIdList}-${listingRows}`;
    }

    return null;
  };

  currentSectionOfHighlightedSeats = (props) => {
    const {listingSelected} = props;
    if (listingSelected && listingSelected.sectionId) {
      return listingSelected.sectionId;
    }
  };

  sources = (props) => {
    return addMapSource(props);
  };

  layerWithPreview = (layerConfig) => {
    const {mapboxMode} = this.props;
    if (layerConfig && typeof layerConfig === 'object') {
      if (typeof layerConfig['source-layer'] === 'string') {
        layerConfig['source-layer'] = urlWithMode(layerConfig['source-layer'], mapboxMode);
      }
    }

    return layerConfig;
  };

  layers = (props) => {
    const {type, mapConfigData, availableRows, availableSeats, listingSelected, sectionColors} = props;
    let layerAdded = {};
    if (type === TYPE_ROW) {
      layerAdded = new Layer({
        config: this.layerWithPreview(
          RowLayers.LayerRows(mapConfigData, venueConfigId(props), availableRows, [this.selectedRow()])
        ),
      });
    } else if (type === TYPE_SEAT) {
      layerAdded = new Layer({
        config: SeatLayers.LayerSeatsHighlight(
          mapConfigData,
          venueConfigId(props),
          availableSeats,
          getUniqueSeatIdsFromListing(this.props ? listingSelected : null)
        ),
      });
    }

    return [
      new Layer({
        config: this.layerWithPreview(BackgroundLayers.layerBackground(mapConfigData)),
      }),
      layerAdded,
      new Layer({
        config: this.layerWithPreview(
          SectionLayers.LayerSectionsHighlight(
            mapConfigData,
            venueConfigId(props),
            COLOR_UNAVAILABLE_SECTION,
            this.availableSections(),
            this.selectedSectionIdList(),
            sectionColors
          )
        ),
        beforeLayerId: [LAYER_SEATS_HIGHLIGHTED_ID, LAYER_ROWS_ID],
      }),
      new Layer({
        config: this.layerWithPreview(SectionLayers.LayerSectionsHover(mapConfigData, venueConfigId(props))),
      }),
      new Layer({
        config: this.layerWithPreview(SectionLayers.LayerSectionsOutline(mapConfigData, venueConfigId(props))),
      }),
      new Layer({
        config: this.layerWithPreview(
          SectionLayers.LayerGASections(
            mapConfigData,
            venueConfigId(props),
            COLOR_NONGA_SECTION,
            Object.keys(this.GASections),
            [this.selectedGASectionId()]
          )
        ),
        beforeLayerId: [
          LAYER_SECTIONS_OUTLINE_ID,
          LAYER_ROWS_ID,
          LAYER_SEATS_HIGHLIGHTED_ID,
          LAYER_SECTIONS_HIGHLIGHTED,
        ],
      }),
      new Layer({
        config: LabelLayers.layerLabels(mapConfigData),
      }),
    ];
  };

  // TODO: maybe move this to the controller
  // eslint-disable-next-line react/no-unused-class-component-methods
  get guestCheckout() {
    const {globalRegistry} = this.context || {};

    return globalRegistry && globalRegistry.getFeatureValue('common.guestXO') ? '&gxo=1' : '';
  }

  get renderedSections() {
    const features = this.actions.queryRenderedFeatures({layers: [LAYER_SECTIONS_OUTLINE_ID]});
    const renderedSectionsArray = features.map((feature) => feature.properties.sectionId);
    renderedSectionsArray.push(this.currentSectionOfHighlightedSeats(this.props));

    return renderedSectionsArray.join(',');
  }

  get currentZoomLayerID() {
    const {mapConfigData} = this.props;
    const currentZoom = this.mapInstance.getZoom();
    let layerID = '';
    if (currentZoom > mapConfigData.ZOOM_MAX_SECTIONS) {
      layerID = LAYER_SEATS_ID;
    } else {
      layerID = LAYER_SECTIONS_ID;
    }

    return layerID;
  }

  updateSeatInventory = (props, sectionIds) => {
    const {getQuery, shstore} = props;
    const {fetchSeatInventoryListings} = this.props;

    if (!sectionIds) {
      sectionIds = this.renderedSections;
    }
    fetchSeatInventoryListings(props.eventId, {...getQuery(props), sectionIdList: sectionIds, shstore});
  };

  updateSectionStats = (props) => {
    const {getQuery, shstore} = props;
    const {fetchSectionStats} = this.props;
    if (getQuery) {
      fetchSectionStats(props.eventId, {...getQuery(props), shstore});
    }
  };

  updateRender = () => {
    const {type} = this.props;
    switch (this.currentZoomLayerID) {
      case LAYER_SEATS_ID:
        if (type === TYPE_SEAT) {
          this.updateHighlightedSeatsStyle();
        } else if (type === TYPE_ROW) {
          this.updateHighlightedRowsStyle();
        }
        this.updateGASectionsStyle();
        this.updateHighlightedSectionsStyle();
        break;
      case LAYER_SECTIONS_ID:
        this.updateHighlightedSectionsStyle();
        this.updateGASectionsStyle();
        break;
      default:
        break;
    }
    this.needsUpdateRender = false;
  };

  updateHighlightedSectionsStyle = () => {
    const {mapType, listingInfo, sectionColors} = this.props;

    if (layerExists(this.mapInstance, LAYER_SECTIONS_HIGHLIGHTED)) {
      if (mapType === MAP_TYPE.ZONE && !listingInfo) {
        this.updateHighlightedZonesStyle();
      } else {
        const availableSectionsStyle = sectionsStylesGenerator(
          this.availableSections(),
          this.selectedSectionIdList(),
          this.hoveredSectionIdList(),
          COLOR_UNAVAILABLE_SECTION,
          sectionColors
        );
        this.mapInstance.setPaintProperty(LAYER_SECTIONS_HIGHLIGHTED, 'fill-color', availableSectionsStyle.fill);
        this.mapInstance.setPaintProperty(LAYER_SECTIONS_OUTLINE_ID, 'line-color', availableSectionsStyle.line);
        this.mapInstance.setPaintProperty(LAYER_SECTIONS_OUTLINE_ID, 'line-width', availableSectionsStyle.width);
        this.mapInstance.setPaintProperty(LAYER_SECTIONS_HOVERED, 'fill-opacity', availableSectionsStyle.hover);
      }
    }
  };

  getSectionIdsFromZones = (zoneList) => {
    const {availableZonesSections} = this.props;
    const sections = getAvailableSectionsForZones(zoneList, availableZonesSections);

    return sections && sections.length ? sections : [];
  };

  updateHighlightedZonesStyle = () => {
    const {zoneIdList, sectionColors} = this.props;
    const selectedSectionIds = this.getSectionIdsFromZones(zoneIdList);
    const availableSectionsStyle = sectionsStylesGenerator(
      this.availableSections(),
      selectedSectionIds,
      this.hoveredSectionIdList(),
      COLOR_UNAVAILABLE_SECTION,
      sectionColors
    );
    this.mapInstance.setPaintProperty(LAYER_SECTIONS_HIGHLIGHTED, 'fill-color', availableSectionsStyle.fill);
    this.mapInstance.setPaintProperty(LAYER_SECTIONS_OUTLINE_ID, 'line-color', availableSectionsStyle.line);
    this.mapInstance.setPaintProperty(LAYER_SECTIONS_OUTLINE_ID, 'line-width', availableSectionsStyle.width);
    this.mapInstance.setPaintProperty(LAYER_SECTIONS_HOVERED, 'fill-opacity', availableSectionsStyle.hover);
  };

  updateGASectionsStyle = () => {
    if (Object.keys(this.GASections).length > 0 && layerExists(this.mapInstance, LAYER_SECTIONS_GA)) {
      const GASectionsStyle = sectionsStylesGenerator(
        Object.keys(this.GASections),
        this.selectedGASectionId(),
        this.hoveredSectionIdList(),
        COLOR_NONGA_SECTION
      );
      this.mapInstance.setPaintProperty(LAYER_SECTIONS_GA, 'fill-color', GASectionsStyle.fill);
    }
  };

  updateHighlightedSeatsStyle = () => {
    if (layerExists(this.mapInstance, LAYER_SEATS_HIGHLIGHTED_ID)) {
      const {availableSeats, listingSelected} = this.props;
      const highlightedSeatsStyle = seatsCircleColor(availableSeats, getUniqueSeatIdsFromListing(listingSelected));
      this.mapInstance.setPaintProperty(LAYER_SEATS_HIGHLIGHTED_ID, 'circle-color', highlightedSeatsStyle);
    }
  };

  updateHighlightedRowsStyle = () => {
    if (layerExists(this.mapInstance, LAYER_ROWS_ID)) {
      const {availableRows} = this.props;
      const highlightedRowsStyle = rowsFillColor(availableRows, [this.selectedRow()]);
      this.mapInstance.setPaintProperty(LAYER_ROWS_ID, 'line-color', highlightedRowsStyle);
    }
  };

  stateSelectedFromTicketList = (nextProps) => {
    const nextListingSelected = nextProps.listingSelected;
    const {listingSelected, type, updateQuery, rowCenteredOnMap, setStateFromObject} = this.props;
    if (!listingSelected && nextListingSelected && nextListingSelected.seats[0].listingId) {
      setStateFromObject({
        seatClickedFromMap: true,
        sectionClicked: false,
      });

      const {sectionId, row, seats, listingId} = seatProps(nextProps);
      const [{seatNumber}] = seats;

      if (type === TYPE_SEAT) {
        if (!this.clickTooltip.isOpen()) {
          this.flyToFeatureFromTicketList(sectionId, row, seatNumber, listingId, nextListingSelected);
          updateQuery({
            sectionIdList: false,
          });
        }
      } else if (type === TYPE_ROW) {
        if (!rowCenteredOnMap) {
          this.flyToFeatureFromTicketList(sectionId, row, seatNumber, listingId, nextListingSelected);
        }
        setStateFromObject({
          rowClickedFromList: `${sectionId}-${row}`,
        });
      }
    }
  };

  flyToFeatureFromTicketList = (sectionId, row, seatNumber, listingId, nextListingSelected) => {
    const {type, mapConfigData, setRowCenteredOnMapState, setStateFromObject} = this.props;

    const {flyToFeature} = this.actions;
    const uniqueId = type === TYPE_ROW ? `${sectionId}-${row}` : `${sectionId}-${row}-${seatNumber}`;
    const searchId = type === TYPE_ROW ? `${sectionId}-${row}` : listingId;
    const featureLists = type === TYPE_ROW ? this.rowFeaturesCache : this.listingsCenterCoordCache;
    const fitBoundsZoomLevel =
      type === TYPE_ROW ? mapConfigData.ZOOM_MAX_FITBOUNDS_ROWS : mapConfigData.ZOOM_MAX_FITBOUNDS_SEATS;
    const oneFeature = featureLists[searchId];
    const layerId = type === TYPE_ROW ? LAYER_ROWS_ID : LAYER_SEATS_HIGHLIGHTED_ID;
    this.featureRecorder = type === TYPE_ROW ? this.recordRowFeatures : this.recordSeatCoords;
    if (oneFeature) {
      if (type === TYPE_SEAT) {
        const uniqueSeatIdArray = getUniqueSeatIdsFromListing(nextListingSelected);
        this.showTooltipAtKnownPoint(oneFeature, uniqueSeatIdArray);
      } else {
        this.actions.flyToFeature(featureLists[uniqueId], fitBoundsZoomLevel);
        setRowCenteredOnMapState(true);
      }
    } else if (this.GASections[sectionId]) {
      flyToFeature(this.sectionFeaturesCache[sectionId], mapConfigData.ZOOM_MAX_FITBOUNDS_GASECTIONS);
      setStateFromObject({
        GASectionIdClickedFromList: sectionId,
      });
      this.mapInstance.once('idle', () => {
        const sectionFeature = this.sectionFeaturesCache[sectionId];
        if (sectionFeature) {
          this.flyToListingSelected(nextListingSelected, uniqueId, sectionFeature);
        }
      });
    } else {
      flyToFeature(this.sectionFeaturesCache[sectionId], fitBoundsZoomLevel);
      this.mapInstance.once('idle', () => {
        const queriedFeatures = this.mapInstance.queryRenderedFeatures({layers: [layerId]});
        this.featureRecorder(queriedFeatures);
        this.flyToListingSelected(nextListingSelected, uniqueId);
      });
    }
  };

  stateNoListingSelected = (nextProps) => {
    const {
      updateQuery,
      listingSelected,
      type,
      sectionClicked,
      setStateFromObject,
      mapConfigData,
      setListingSelected,
      rowClickedFromList,
    } = this.props;
    this.clickTooltip.remove();
    if (!sectionClicked && type === TYPE_SEAT) {
      updateQuery({
        sectionIdList: false,
      });
    }

    if (type === TYPE_SEAT) {
      if (this.mapInstance.getZoom() > mapConfigData.ZOOM_TOOLTIP_BREAKPOINT) {
        this.mapInstance.zoomTo(mapConfigData.ZOOM_TOOLTIP_BREAKPOINT, {duration: 1000});
      } else if (this.mapInstance.getZoom() === mapConfigData.ZOOM_MAX_FITBOUNDS_GASECTIONS) {
        // eslint-disable-next-line no-magic-numbers
        this.mapInstance.zoomTo(this.mapInstance.getZoom() - 0.7, {duration: 1000});
      }
    }

    if (type === TYPE_ROW && nextProps && !nextProps.listingRows && rowClickedFromList) {
      this.actions.flyToHome();
    }

    if (listingSelected) {
      setStateFromObject({
        seatClickedFromMap: false,
        sectionClicked: false,
        GASectionIdClickedFromList: null,
        usidsOfListingSelected: null,
        rowClickedFromList: null,
        rowCenteredOnMap: false,
      });

      setListingSelected(null);
    }
    if (this.currentZoomLayerID !== 'sections') {
      this.mapInstance.once('idle', this.updateRender);
    } else {
      this.needsUpdateRender = true;
    }
  };

  detectListingSelection = (nextProps) => {
    const {listingSelected: previousListing, seatClickedFromMap} = this.props;
    const {listingSelected: nextListing} = nextProps;
    let result = false;

    if (seatClickedFromMap) {
      return false;
    }

    if (previousListing && nextListing) {
      const previousId = getListingIdFromListing(previousListing);
      const currentId = getListingIdFromListing(nextListing);
      if (previousId !== currentId) {
        result = true;
      }
    } else if (!previousListing && nextListing) {
      result = true;
    }

    return result;
  };

  detectQuantityChange = (nextProps) => {
    const {listingSelected, listingId, byoQuantity} = this.props;
    if (
      listingSelected &&
      nextProps.listingSelected &&
      listingId &&
      nextProps.listingId &&
      listingId === nextProps.listingId
    ) {
      return byoQuantity !== nextProps.byoQuantity;
    }
  };

  detectIfBYOQuantityIsNotInSplitVector = (nextProps) => {
    const {listingSelected} = nextProps;
    if (listingSelected) {
      return !(listingSelected.splitVector.includes(nextProps.byoQuantity) === true);
    }

    return false;
  };

  handlelistingsReloaded = (nextProps) => {
    const {type, listingLoading, setListingSelected} = this.props;
    if (type === TYPE_SEAT) {
      const listingInfo = seatProps(this.props);
      if (listingInfo) {
        const {sectionId, seats} = listingInfo;
        const [{seatNumber, row}] = seats;
        let listing = {};
        if (nextProps.type === TYPE_ROW) {
          listing = getListingsFromSectionRowIds(sectionId, row, nextProps);
        } else {
          listing = getListingFromSectionRowSeatIds(sectionId, row, seatNumber, nextProps);
        }
        if (listing && listingLoading && !nextProps.listingLoading) {
          setListingSelected(listing);
        }
        this.handlePriceWithFees(nextProps);
      }
    }
  };

  handlePriceWithFees = (nextProps) => {
    const {listingSelected, renderToolTip} = this.props;
    if (
      listingSelected &&
      nextProps.listingSelected &&
      listingSelected.price &&
      listingSelected.price &&
      listingSelected.price.amount &&
      nextProps.listingSelected.price.amount
    ) {
      if (listingSelected.price.amount !== nextProps.listingSelected.price.amount && this.clickTooltip.isOpen()) {
        renderToolTip(nextProps);
      }
    }
  };

  // eslint-disable-next-line react/no-unused-class-component-methods
  handleQuantitySelection = (qtySelected) => {
    const {updateQuery} = this.props;
    updateQuery({
      byoQuantity: qtySelected,
    });
  };

  handleSectionClick = (e) => {
    const {mapType, availableSections, listingInfo, processSectionClicked, processZoneClicked} = this.props;
    if (!listingInfo) {
      const sectionLayerFeatures = this.actions.queryRenderedFeatures(e.point, {
        layers: [LAYER_SECTIONS_HIGHLIGHTED],
        filter: ['in', 'sectionId', ...availableSections],
      });
      const [firstFeature] = sectionLayerFeatures;
      if (mapType === 'zone') {
        processZoneClicked(firstFeature);
      } else {
        processSectionClicked(firstFeature);
      }
    }
  };

  handleGASectionClick = (e) => {
    const {processSectionClicked} = this.props;
    const GASectionLayerFeatures = this.actions.queryRenderedFeatures(e.point, {
      layers: [LAYER_SECTIONS_GA],
      filter: ['in', 'sectionId', ...Object.keys(this.GASections)],
    });
    const [firstFeature] = GASectionLayerFeatures;
    processSectionClicked(firstFeature);
  };

  handleRowOnHover = (e) => {
    const {availableRows} = this.props;
    const rowFeatures = this.mapInstance.queryRenderedFeatures(
      tapArea(e.point, this.tapPaddingWidth, this.tapPaddingHeight),
      {layers: [LAYER_ROWS_ID], filter: ['in', 'urid', ...(availableRows || [])]}
    );
    const [firstFeature] = rowFeatures;
    if (firstFeature && firstFeature.properties && firstFeature.properties.urid) {
      const {urid} = firstFeature.properties;
      this.renderRowToolTip(e, urid);

      this.mapInstance.getCanvas().style.cursor = 'pointer';
      const highlightedRowsStyle = rowsFillColor(availableRows, [this.selectedRow()], [urid]);
      this.mapInstance.setPaintProperty(LAYER_ROWS_ID, 'line-color', highlightedRowsStyle);
    }
  };

  handleRowOffHover = () => {
    const {availableRows} = this.props;
    if (this.rowTooltip.isOpen()) {
      this.rowTooltip.remove();
    }
    this.mapInstance.getCanvas().style.cursor = '';
    const highlightedRowsStyle = rowsFillColor(availableRows, [this.selectedRow()]);
    this.mapInstance.setPaintProperty(LAYER_ROWS_ID, 'line-color', highlightedRowsStyle);
  };

  handleByoQuantityUpdate = (nextProps) => {
    const {renderToolTip} = this.props;
    const listingSectionRowSeatArray = getUniqueSeatIdsFromListing(nextProps.listingSelected);
    if (listingSectionRowSeatArray && this.clickTooltip.isOpen()) {
      renderToolTip(nextProps);
    }
    this.needsUpdateRender = true;
  };

  handleListingSelection = (nextProps) => {
    const {setPreviousListingSelected, mapConfigData} = this.props;
    switch (this.currentZoomLayerID) {
      case LAYER_SEATS_ID:
        this.stateSelectedFromTicketList(nextProps);
        break;
      case LAYER_SECTIONS_ID:
        this.mapInstance.setMaxZoom(mapConfigData.ZOOM_MAX_SEATS);
        this.stateSelectedFromTicketList(nextProps);
        setPreviousListingSelected(nextProps.listingSelected);
        break;
      case LAYER_ROWS_ID:
        break;
      default:
        break;
    }
  };

  handleFilterChange = (nextProps) => {
    const {setPreviousListingSelected, byoQuantity} = this.props;
    const {listingId, ...options} = nextProps;
    switch (this.currentZoomLayerID) {
      case LAYER_SEATS_ID:
        if (nextProps.quantity !== byoQuantity) {
          setPreviousListingSelected(nextProps.listingSelected);
        }
        this.updateSeatInventory(options, this.renderedSections);
        this.needsUpdateRender = true;
        break;
      case LAYER_SECTIONS_ID:
        break;
      case LAYER_ROWS_ID:
        break;
      default:
        break;
    }
  };

  handleByoUpdateToMatchSplitVector = (nextProps) => {
    const {updateQuery} = this.props;
    const {splitVector} = nextProps.listingSelected;
    updateQuery({
      byoQuantity: splitVectorMinimumQuantity(splitVector),
    });
  };

  handleClick = (e) => {
    const {type} = this.props;
    switch (this.currentZoomLayerID) {
      case LAYER_SEATS_ID:
        if (type === TYPE_SEAT) {
          this.handleHighlightedSeatClick(e);
        } else if (type === TYPE_ROW) {
          this.handleRowClick(e);
        }
        this.handleGASectionClick(e);
        break;
      case LAYER_SECTIONS_ID:
        this.handleSectionClick(e);
        break;
      default:
        break;
    }
  };

  flyToListingSelected = (listingSelected, uniqueId, GASectionFeature) => {
    const {type, setRowCenteredOnMapState} = this.props;
    if (type === TYPE_SEAT) {
      const seatFeature = getSeatFeatureByUniqueSeatId(this.mapInstance, uniqueId);
      if (seatFeature) {
        const uniqueSeatIdArray = getUniqueSeatIdsFromListing(listingSelected);
        this.showToolTipAtFeature(seatFeature, this.seatCoordCache, uniqueSeatIdArray);
      } else if (GASectionFeature) {
        this.showToolTipAtFeature(GASectionFeature);
      }
    } else {
      const rowFeature = this.rowFeaturesCache[uniqueId]
        ? this.rowFeaturesCache[uniqueId]
        : getRowFeatureByUniqueRowId(this.mapInstance, uniqueId);
      if (rowFeature) {
        this.actions.flyToFeature(rowFeature);
        setRowCenteredOnMapState(true);
      } else if (GASectionFeature) {
        this.showToolTipAtFeature(GASectionFeature);
      }
    }
  };

  showTooltipAtKnownPoint = (centerCoords, uniqueSeatIdArray) => {
    const {device, setStateFromObject, renderToolTip} = this.props;
    this.actions.centerAtAPoint(centerCoords, device);
    this.clickTooltip.setLngLat(centerCoords);
    this.mapInstance.once('idle', () => {
      setStateFromObject({
        usidsOfListingSelected: uniqueSeatIdArray,
      });
      if (this.detectIfBYOQuantityIsNotInSplitVector(this.props)) {
        this.handleByoUpdateToMatchSplitVector(this.props);
      }
      renderToolTip(this.props);
    });
  };

  showToolTipAtFeature = (feature, seatCoords, uniqueSeatIdArray) => {
    const {setToolTipPosition, type, renderToolTip, setStateFromObject} = this.props;
    this.actions.centerOnFeature(feature, seatCoords, uniqueSeatIdArray);

    setToolTipPosition(feature.geometry.coordinates, feature, uniqueSeatIdArray);
    if (type === TYPE_SEAT) {
      setStateFromObject({
        usidsOfListingSelected: uniqueSeatIdArray,
      });
    }
    if (this.detectIfBYOQuantityIsNotInSplitVector(this.props)) {
      this.handleByoUpdateToMatchSplitVector(this.props);
    }
    this.mapInstance.once('idle', () => {
      renderToolTip(this.props);
    });
  };

  // eslint-disable-next-line react/no-unused-class-component-methods
  handleCheckout = (event) => {
    const {goToCheckout} = this.props;
    goToCheckout && goToCheckout(event);
  };

  // eslint-disable-next-line react/no-unused-class-component-methods
  handleContinueButtonClick = () => {
    const {highlightedSeatsQueryUpdate} = this.props;
    const {listingId, listingSelected} = seatProps(this.props);
    highlightedSeatsQueryUpdate(listingId, listingSelected);
  };

  addResetControl = () => {
    const {hasReset, setHasResetState} = this.props;
    if (!hasReset) {
      this.mapInstance.addControl(this.actions.resetControl, 'bottom-right');
      setHasResetState(true);
    }
  };

  removeResetControl = () => {
    const {setHasResetState} = this.props;
    this.mapInstance.removeControl(this.actions.resetControl);
    setHasResetState(false);
  };

  shouldResetButtonAdd = () => {
    const {sectionIdList, zoneIdList, listingRows, showBYO, listingId} = this.props;

    return this.actions.isMapMoved() || sectionIdList || zoneIdList || listingRows || showBYO || listingId;
  };

  componentDidMount() {
    const {venueConfig, seatMapsWrapperActions} = this.props;
    if (venueConfig.id) {
      this.updateSectionStats(this.props);
      this.initMap();
      seatMapsWrapperActions(this); // TODO: restrict interface to only allowed
    }
  }

  getZoneIdsFromSections = (sectionList) => {
    const {mapSectionStats} = this.props;
    const selectedZones = [];
    mapSectionStats.forEach((item) => {
      if (
        sectionList === item.sectionId ||
        (Array.isArray(sectionList) &&
          sectionList.includes(item.sectionId.toString()) &&
          !selectedZones.includes(item.zoneId))
      ) {
        selectedZones.push(item.zoneId);
      }
    });

    return selectedZones;
  };

  UNSAFE_componentWillReceiveProps(nextProps) {
    const {
      mapType,
      showBYO,
      hoveredListingSid,
      hoveredListingZid,
      device,
      updateQuery,
      sectionIdList,
      dispatchFilterChangedHandled,
      listingLoading,
    } = this.props;

    if (nextProps.mapType === 'zone' && (mapType === 'section' || mapType === undefined) && nextProps.mapSectionStats) {
      updateQuery(
        {
          sectionIdList: null,
          zoneIdList: this.getZoneIdsFromSections(sectionIdList),
        },
        true
      );
    }
    if (device === 'desktop') {
      let action = 'none';
      if (this.detectListingSelection(nextProps)) {
        action = 'listingSelected';
      } else if (nextProps.showBYO !== true && showBYO === true) {
        action = 'listingDeSelected';
      } else if (nextProps.filterChanged) {
        action = 'filterChange';
      } else if (this.detectQuantityChange(nextProps)) {
        action = 'quantityUpdate';
      } else if (nextProps.hoveredListingSid !== hoveredListingSid) {
        action = 'listingSectionHovered';
      } else if (nextProps.hoveredListingZid !== hoveredListingZid) {
        action = 'listingZoneHovered';
      } else {
        action = 'inventoryUpdated';
      }

      switch (action) {
        case 'listingSelected':
          this.handleListingSelection(nextProps);
          break;
        case 'inventoryUpdated':
          this.handlelistingsReloaded(nextProps);
          if (listingLoading && !nextProps.listingLoading) {
            this.recordListingsCenter(nextProps.mapSectionRowSeatIdsToListingId);
          }
          this.needsUpdateRender = true;
          break;
        case 'filterChange':
          this.handleFilterChange(nextProps);
          this.updateSectionStats(nextProps);
          dispatchFilterChangedHandled && dispatchFilterChangedHandled();
          this.needsUpdateRender = true;
          break;
        case 'listingDeSelected':
          this.stateNoListingSelected(nextProps);
          break;
        case 'quantityUpdate':
          this.handleByoQuantityUpdate(nextProps);
          break;
        case 'listingSectionHovered':
        case 'listingZoneHovered':
          this.needsUpdateRender = true;
          break;
        default:
          this.needsUpdateRender = true;
      }
    } else if (device === 'phone') {
      if (!nextProps.listingSelected) {
        this.clickTooltip.remove();
      }
      if (listingLoading && !nextProps.listingLoading) {
        this.recordListingsCenter(nextProps.mapSectionRowSeatIdsToListingId);
      }
      this.needsUpdateRender = true;
    }
  }

  componentDidUpdate(prevProps) {
    if (!venueConfigId(prevProps) && venueConfigId(this.props)) {
      this.updateSectionStats(this.props);
      this.initMap();
    }
    if (this.needsUpdateRender) {
      this.updateRender();
    }
  }

  render() {
    const {listingInfo, mapConfigData, defaultConfig} = this.props;
    if (venueConfigId(this.props)) {
      return (
        <div className="SeatMapView__Mapbox">
          <Map
            container={listingInfo ? MAPBOX_DOM_CONTAINER_ID_BYOVIEW : MAPBOX_DOM_CONTAINER_ID}
            onClick={this.handleClick}
            mapInstanceRef={(mapInstance) => (this.mapInstance = mapInstance)}
            mapActions={(actions) => (this.actions = actions)}
            sources={this.sources(this.props)}
            layers={this.layers(this.props)}
            mapConfigData={mapConfigData}
            defaultConfig={defaultConfig}
            clickTooltip={this.clickTooltip}
            removeResetControl={this.removeResetControl}
            addResetControl={this.addResetControl}
            {...this.props}
          />
        </div>
      );
    }

    return <Spinner />;
  }
}

SeatMapMapboxView.propTypes = {
  /** The GASectionIdClickedFromList. */
  GASectionIdClickedFromList: PropTypes.any,

  /** The available rows. */
  availableRows: PropTypes.array,

  /** The available seats. */
  availableSeats: PropTypes.array,

  /** The available sections. */
  availableSections: PropTypes.array,

  /** The available sections lookup. */
  availableSectionsLookUp: PropTypes.object,

  /** The available zones sections. */
  availableZonesSections: PropTypes.object,

  /** The byo quantity. */
  byoQuantity: PropTypes.number,

  /** The default config. */
  defaultConfig: PropTypes.object,

  /** The device type. */
  device: PropTypes.string,

  /** The dispatch filter changed handled. */
  dispatchFilterChangedHandled: PropTypes.func,

  /** The event ID. */
  eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /** The function to fetch seat inventory listings. */
  fetchSeatInventoryListings: PropTypes.func,

  /** The function to fetch section stats. */
  fetchSectionStats: PropTypes.func,

  /** The function to fetch the selected listing. */
  fetchSelectedListing: PropTypes.func,

  /** The function to generate the popup. */
  generatePopup: PropTypes.func,

  /** The function to get the query. */
  getQuery: PropTypes.func,

  /** The function to go to checkout. */
  goToCheckout: PropTypes.func,

  /** The function to handle mouse move */
  handleSectionMouseMove: PropTypes.func,

  /** The function to handle mouse leave */
  handleSectionMouseLeave: PropTypes.func,

  /** The function to update query with highlight seats */
  highlightedSeatsQueryUpdate: PropTypes.func,

  /* Has reset functionality */
  hasReset: PropTypes.bool,

  /** The hovered listing sid. */
  hoveredListingSid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /** The hovered listing zid. */
  hoveredListingZid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /** The hovered sections. */
  hoveredSections: PropTypes.array,

  /** The listing ID. */
  listingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /** The listing info. */
  listingInfo: object,

  /** The listing loading. */
  listingLoading: PropTypes.bool,

  /** The listing rows. */
  listingRows: PropTypes.string,

  /** The listing selected. */
  listingSelected: PropTypes.object,

  /** The map config data. */
  mapConfigData: PropTypes.object,

  /** The map section stats. */
  mapSectionStats: PropTypes.array,

  /** The map type. */
  mapType: PropTypes.string,

  /** The mapbox mode. */
  mapboxMode: PropTypes.string,

  /** The function to process the section clicked. */
  processSectionClicked: PropTypes.func,

  /** The function to process the zone clicked. */
  processZoneClicked: PropTypes.func,

  /** The function to render the tooltip. */
  renderToolTip: PropTypes.func,

  /** The row centered on map. */
  rowCenteredOnMap: PropTypes.bool,

  /** The row clicked from list. */
  rowClickedFromList: PropTypes.string,

  /** The seat clicked from map. */
  seatClickedFromMap: PropTypes.bool,

  /** The seat maps wrapper actions. */
  seatMapsWrapperActions: PropTypes.func,

  /** The section clicked. */
  sectionClicked: PropTypes.bool,

  /** The section colors. */
  sectionColors: PropTypes.object,

  /** The section ID list. */
  sectionIdList: PropTypes.array,

  /** The function to set the has reset state. */
  setHasResetState: PropTypes.func,

  /** The function to set the listing selected. */
  setListingSelected: PropTypes.func,

  /** The function to set the loaded state. */
  setLoadedState: PropTypes.func,

  /** The function to set the previous listing selected. */
  setPreviousListingSelected: PropTypes.func,

  /** The function to set the row centered on map state. */
  setRowCenteredOnMapState: PropTypes.func,

  /** The function to set the state from object. */
  setStateFromObject: PropTypes.func,

  /** The function to set the tooltip position. */
  setToolTipPosition: PropTypes.func,

  /** The show BYO. */
  showBYO: PropTypes.bool,

  /** The shstore. */
  shstore: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /** The type. */
  type: PropTypes.string,

  /** The function to update the query. */
  updateQuery: PropTypes.func,

  /** The venue config. */
  venueConfig: PropTypes.object,

  /** The zone ID list. */
  zoneIdList: PropTypes.any,
};

export default injectIntl(SeatMapMapboxView);
